import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import classNames from 'classnames';
import { useState, createContext } from 'react';
import { getAccessibleColor, getRGBColor } from 'utils/layout';
import { getSizedImage } from 'utils/images';
import { FormattedMessage, useIntl } from 'react-intl';
import styles from './Bot.module.css';
import { ChatWidget } from './components/ChatWidget/index.js';
import { ChatForm } from './components/ChatForm/index.js';
import { ChatBubble } from './components/ChatBubble/index.js';
import { ItemListSkill } from './components/ItemList.skill/index.js';
import ReactMarkdown from 'react-markdown';
import { getBookingEngineUrl } from '../../utils/url.js';
import { getSiteUrl } from '../../utils/siteConfig.js';
import { Amount } from 'ui';
import { ItinerarySkill } from './components/Itinerary.skill/index.js';
import { MapSkill } from './components/Map.skill/index.js';
import { useMobileScrollLock } from './hooks/useMobileScrollLock.js';
export const BotContext = createContext({});
export const Bot = ({ history, onSubmit, isFetching, position = 'bottom-right', offset, useTailwindPrefix, saveCSSVarColorsWithotPrefix, options: baseOptions, notification, onSkillSelect, onHotelSelect, onDestinationSelect, theme, defaultOpen = false, bucket = 'hotel-stage', bookingEngineVersion = 2, forceShowHotel, siteConfig, onDrawHotelRate, onRateConversation, isRatingConversation, ratingStatus, ratings, }) => {
    const intl = useIntl();
    const [isOpen, setIsOpen] = useState(defaultOpen);
    useMobileScrollLock(isOpen);
    const options = Object.assign({}, baseOptions, {
        name: (baseOptions === null || baseOptions === void 0 ? void 0 : baseOptions.name) || 'MaIA',
        avatar: (baseOptions === null || baseOptions === void 0 ? void 0 : baseOptions.avatar) || {
            base: 'https://res.cloudinary.com/itermotus/assets',
            path: '/chat/M0.jpg',
        },
    });
    const onToggle = () => {
        setIsOpen(!isOpen);
    };
    const mod_getRGBColor = (hex, name) => {
        if (saveCSSVarColorsWithotPrefix) {
            return getRGBColor(hex, name, '');
        }
        return getRGBColor(hex, name);
    };
    const getStyleOptions = ({ primaryColor, accessiblePrimaryColor, offset, }) => {
        const values = [];
        values.push(mod_getRGBColor(primaryColor, 'bot-accent'));
        values.push(getRGBColor(primaryColor, 'itm-bot-accent', ''));
        values.push(mod_getRGBColor(accessiblePrimaryColor || getAccessibleColor(primaryColor, { light: '#FFFFFF', dark: '#3e4551' }), 'bot-accent-a11y'));
        values.push(`--bot-offset-x: ${(offset === null || offset === void 0 ? void 0 : offset.x) || 0}px;`);
        values.push(`--bot-offset-y: ${(offset === null || offset === void 0 ? void 0 : offset.y) || 0}px;`);
        values.push(`--bot-offset-x-mobile: ${(offset === null || offset === void 0 ? void 0 : offset.x) || 0}px;`);
        values.push(`--bot-offset-y-mobile: 70px;`);
        return values;
    };
    const drawAvatar = () => {
        var _a;
        return options.avatar ? (_jsx("img", { src: getSizedImage(options.avatar.base + options.avatar.path, {
                h: 40,
                silhouette: 'CIRCLE',
                fit: 'thumb',
            }), srcSet: `
            ${getSizedImage(options.avatar.base + options.avatar.path, {
                h: 40,
                silhouette: 'CIRCLE',
                fit: 'thumb',
                dpr: '2.0',
            })} 2x
         `, alt: options.name, className: classNames({
                'w-[40px] h-[40px]': !useTailwindPrefix,
                'tw-w-[40px] tw-h-[40px]': useTailwindPrefix,
            }) })) : (_jsx("div", Object.assign({ className: classNames({
                'rounded-full h-[40px] w-[40px] bg-bot-accent-a11y text-bot-accent flex items-center justify-center': !useTailwindPrefix,
                'tw-rounded-full tw-h-[40px] tw-w-[40px] tw-bg-bot-accent-a11y tw-text-bot-accent tw-flex tw-items-center tw-justify-center': useTailwindPrefix,
            }) }, { children: ((_a = options.name) === null || _a === void 0 ? void 0 : _a.charAt(0).toUpperCase()) || 'M' })));
    };
    const drawSkills = (msg) => {
        var _a, _b;
        if (!msg.data) {
            return null;
        }
        if (msg.data.skills && msg.data.skills.length > 0 && onSkillSelect) {
            return (_jsx("div", Object.assign({ className: classNames({
                    'flex flex-wrap gap-2 w-full justify-end': !useTailwindPrefix,
                    'tw-flex tw-flex-wrap tw-gap-2 tw-w-full tw-justify-end': useTailwindPrefix,
                }) }, { children: msg.data.skills.map((skill, index) => (_jsx("button", Object.assign({ type: "button", className: classNames({
                        'px-3 py-2 rounded-full border border-bot-accent text-bot-accent hover:bg-bot-accent hover:text-bot-accent-a11y transition': !useTailwindPrefix,
                        'tw-px-3 tw-py-2 tw-rounded-full tw-border tw-border-bot-accent tw-text-bot-accent hover:tw-bg-bot-accent hover:tw-text-bot-accent-a11y tw-transition': useTailwindPrefix,
                    }), onClick: () => onSkillSelect({ prompt: skill.prompt, code: skill.code }) }, { children: skill.name }), index))) })));
        }
        else if (msg.data.map) {
            return _jsx(MapSkill, Object.assign({}, msg.data.map));
        }
        else if (msg.data.restaurants) {
            return (_jsx(ItemListSkill, { items: msg.data.restaurants.map((restaurant) => ({
                    title: restaurant.name,
                    subtitle: '12:00 PM - 11:00 PM',
                    description: restaurant.description,
                    imageUrl: restaurant.imageUrl,
                    url: restaurant.url,
                })) }));
        }
        else if (msg.data.promotions) {
            return (_jsx(ItemListSkill, { items: msg.data.promotions.map((promotion) => (Object.assign(Object.assign({ title: promotion.title, description: () => (_jsxs("div", { children: [_jsx("p", Object.assign({ className: classNames({
                                    'font-semibold text-gray-700 mb-1': !useTailwindPrefix,
                                    'tw-font-semibold tw-text-gray-700 tw-mb-1': useTailwindPrefix,
                                }) }, { children: intl.formatMessage({ id: 'maia_chatbot_promotion_includes' }) })), promotion.includesDescription && (_jsx("ul", Object.assign({ className: classNames({
                                    'mb-2 list-disc list-inside space-y-0.5 text-sm text-gray-900': !useTailwindPrefix,
                                    'tw-mb-2 tw-list-disc tw-list-inside tw-space-y-0.5 tw-text-sm tw-text-gray-900': useTailwindPrefix,
                                }) }, { children: promotion.includesDescription.map((item, index) => (_jsx("li", { children: item }, index))) }))), promotion.importantDates && (_jsxs(_Fragment, { children: [_jsx("p", Object.assign({ className: classNames({
                                            'font-semibold text-gray-700 mb-1': !useTailwindPrefix,
                                            'tw-font-semibold tw-text-gray-700 tw-mb-1': useTailwindPrefix,
                                        }) }, { children: intl.formatMessage({ id: 'maia_chatbot_promotion_terms_and_conditions' }) })), _jsx("ul", Object.assign({ className: classNames({
                                            'mb-2 space-y-0.5 text-sm text-gray-900': !useTailwindPrefix,
                                            'tw-mb-2 tw-space-y-0.5 tw-text-sm tw-text-gray-900': useTailwindPrefix,
                                        }) }, { children: promotion.importantDates.map((item, index) => (_jsx("li", { children: item }, index))) }))] }))] })) }, (promotion.photos &&
                    promotion.photos.length > 0 && {
                    imageUrl: getSizedImage(typeof promotion.photos[0] === 'string'
                        ? `https://res.cloudinary.com/itermotus/${bucket}/${promotion.photos[0]}`
                        : promotion.photos[0].base + promotion.photos[0].path, {
                        h: 126,
                        dpr: '2.0',
                        fit: 'crop',
                    }),
                })), { url: (() => {
                        if (!siteConfig)
                            return null;
                        const lastTravelDate = promotion.lastTravelWindowDate
                            ? new Date(promotion.lastTravelWindowDate)
                            : null;
                        const startDate = lastTravelDate
                            ? new Date(lastTravelDate.getTime() - 2 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)
                            : '';
                        const bookingEngineUrl = getSiteUrl(siteConfig);
                        const hasMultipleHotels = siteConfig.bookerPlaces && siteConfig.bookerPlaces.length > 1;
                        return getBookingEngineUrl({
                            bookingEngineUrl: bookingEngineUrl,
                            hotelId: promotion.hotelId,
                            showHotel: forceShowHotel || hasMultipleHotels,
                            startDate: startDate,
                            endDate: lastTravelDate ? lastTravelDate.toISOString().slice(0, 10) : '',
                            promotionCode: promotion.promotionCode,
                            version: bookingEngineVersion,
                        });
                    })(), buttonLabel: intl.formatMessage({ id: 'maia_chatbot_promotion_see_promotion' }) }))) }));
        }
        else if (msg.data.rates && msg.data.rates.rates) {
            return (_jsx(ItemListSkill, { items: msg.data.rates.rates.map((rate) => {
                    var _a, _b;
                    const result = onDrawHotelRate
                        ? onDrawHotelRate({ roomId: rate.roomId, quoteRequest: (_b = (_a = msg.data) === null || _a === void 0 ? void 0 : _a.rates) === null || _b === void 0 ? void 0 : _b.quoteRequest })
                        : {};
                    return Object.assign(Object.assign({ title: rate.roomName }, (result.photo && {
                        imageUrl: getSizedImage(result.photo.base + result.photo.path, {
                            h: 126,
                            dpr: '2.0',
                            fit: 'crop',
                        }),
                    })), { subtitle: () => {
                            const minRate = rate.ratePlans.reduce((min, current) => (current.totalAmount < min.totalAmount ? current : min), rate.ratePlans[0]);
                            return (_jsx("div", Object.assign({ className: classNames({
                                    'text-gray-500 mb-2': !useTailwindPrefix,
                                    'tw-text-gray-500 tw-mb-2': useTailwindPrefix,
                                }) }, { children: _jsx(FormattedMessage, { id: "maia_chatbot_promotion_min_rate", values: {
                                        minRate: _jsx(Amount, { amount: minRate.totalAmount, currency: minRate.currency }),
                                    } }) })));
                        }, description: () => {
                            return (_jsx(_Fragment, { children: _jsx("div", { children: rate.roomView.name }) }));
                        }, url: (() => {
                            var _a, _b;
                            if (!siteConfig || !((_b = (_a = msg.data) === null || _a === void 0 ? void 0 : _a.rates) === null || _b === void 0 ? void 0 : _b.quoteRequest))
                                return null;
                            const bookingEngineUrl = getSiteUrl(siteConfig);
                            const hasMultipleHotels = siteConfig.bookerPlaces && siteConfig.bookerPlaces.length > 1;
                            return getBookingEngineUrl(Object.assign(Object.assign({ bookingEngineUrl: bookingEngineUrl, hotelId: msg.data.rates.quoteRequest.hotelId, showHotel: forceShowHotel || hasMultipleHotels, startDate: msg.data.rates.quoteRequest.checkIn, endDate: msg.data.rates.quoteRequest.checkOut }, (msg.data.rates.quoteRequest.rooms &&
                                msg.data.rates.quoteRequest.rooms.length > 0 && {
                                rooms: msg.data.rates.quoteRequest.rooms.map((room) => ({
                                    adults: room.adults,
                                    children: room.childrenAges.length,
                                    childrenAges: room.childrenAges.map((age) => ({ age })),
                                })),
                            })), { roomId: rate.roomId, version: bookingEngineVersion }));
                        })(), buttonLabel: intl.formatMessage({ id: 'maia_chatbot_book' }) });
                }) }));
        }
        else if (msg.data.itinerary) {
            if (!siteConfig)
                return null;
            const bookingEngineUrl = getSiteUrl(siteConfig);
            return _jsx(ItinerarySkill, { itinerary: msg.data.itinerary, bookingEngineUrl: bookingEngineUrl });
        }
        else if (((_a = msg.data.dataRequired) === null || _a === void 0 ? void 0 : _a.includes('hotelId')) && msg.data.hotels && onHotelSelect) {
            return (_jsx("div", Object.assign({ className: classNames({
                    'flex flex-wrap gap-4 w-full justify-end': !useTailwindPrefix,
                    'tw-flex tw-flex-wrap tw-gap-4 tw-w-full tw-justify-end': useTailwindPrefix,
                }) }, { children: msg.data.hotels.map((hotel) => (_jsx("button", Object.assign({ type: "button", className: classNames({
                        'px-3 py-2 rounded-full border border-bot-accent text-bot-accent hover:bg-bot-accent hover:text-bot-accent-a11y transition': !useTailwindPrefix,
                        'tw-px-3 tw-py-2 tw-rounded-full tw-border tw-border-bot-accent tw-text-bot-accent hover:tw-bg-bot-accent hover:tw-text-bot-accent-a11y tw-transition': useTailwindPrefix,
                    }), onClick: () => onHotelSelect({ hotelId: hotel.id, prompt: hotel.name }) }, { children: hotel.name }), hotel.id))) })));
        }
        else if (((_b = msg.data.dataRequired) === null || _b === void 0 ? void 0 : _b.includes('destinationId')) && msg.data.destinations && onDestinationSelect) {
            return (_jsx("div", Object.assign({ className: classNames({
                    'flex flex-wrap gap-4 w-full justify-end': !useTailwindPrefix,
                    'tw-flex tw-flex-wrap tw-gap-4 tw-w-full tw-justify-end': useTailwindPrefix,
                }) }, { children: msg.data.destinations.map((destination) => (_jsx("button", Object.assign({ type: "button", className: classNames({
                        'px-3 py-2 rounded-full border border-bot-accent text-bot-accent hover:bg-bot-accent hover:text-bot-accent-a11y transition': !useTailwindPrefix,
                        'tw-px-3 tw-py-2 tw-rounded-full tw-border tw-border-bot-accent tw-text-bot-accent hover:tw-bg-bot-accent hover:tw-text-bot-accent-a11y tw-transition': useTailwindPrefix,
                    }), onClick: () => onDestinationSelect({ destinationId: destination.id, prompt: destination.name }) }, { children: destination.name }), destination.id))) })));
        }
    };
    const colors = getStyleOptions({
        primaryColor: (theme === null || theme === void 0 ? void 0 : theme.primaryColor) || '#417505',
        accessiblePrimaryColor: theme === null || theme === void 0 ? void 0 : theme.accessiblePrimaryColor,
        offset,
    });
    return (_jsxs(BotContext.Provider, Object.assign({ value: {
            useTailwindPrefix,
            position,
            onSubmit,
            isFetching,
            options,
            notification,
            onRateConversation,
            isRatingConversation,
            ratingStatus,
            ratings,
        } }, { children: [_jsx("style", { children: `
            :root {
               ${colors.join(' ')}
            }
         ` }), _jsx("div", Object.assign({ className: classNames(styles.botContainer, Object.assign({ [styles.botContainerLeft]: position === 'bottom-left', [styles.botContainerRight]: position === 'bottom-right' }, (useTailwindPrefix
                    ? {
                        'tw-hidden': isOpen,
                        'tw-fixed tw-z-[9998]': true,
                        'tw-flex tw-justify-end': position === 'bottom-right',
                    }
                    : {
                        hidden: isOpen,
                        'fixed z-[9998]': true,
                        'flex justify-end': position === 'bottom-right',
                    }))) }, { children: _jsx(ChatWidget, { onToggle: onToggle }) })), isOpen && (_jsx("div", Object.assign({ className: classNames(styles.panelWrapper, Object.assign({ [styles.panelWrapperLeft]: position === 'bottom-left', [styles.panelWrapperRight]: position === 'bottom-right' }, (useTailwindPrefix
                    ? { 'tw-flex tw-justify-end': position === 'bottom-right' }
                    : { 'flex justify-end': position === 'bottom-right' }))) }, { children: _jsxs("div", Object.assign({ className: classNames(styles.panel, {
                        'bg-white shadow-2xl !mb-0 flex flex-col': !useTailwindPrefix,
                        'tw-bg-white tw-shadow-2xl tw-!mb-0 tw-flex tw-flex-col': useTailwindPrefix,
                    }) }, { children: [_jsxs("div", Object.assign({ className: classNames({
                                'bg-bot-accent text-bot-accent-a11y rounded-t-[inherit] flex justify-end items-center gap-3 p-4': !useTailwindPrefix,
                                'tw-bg-bot-accent tw-text-bot-accent-a11y tw-rounded-t-[inherit] tw-flex tw-justify-end tw-items-center tw-gap-3 tw-p-4': useTailwindPrefix,
                            }) }, { children: [drawAvatar(), _jsx("h2", Object.assign({ className: classNames({
                                        'text-lg font-semibold flex-1': !useTailwindPrefix,
                                        'tw-text-lg tw-font-semibold tw-flex-1': useTailwindPrefix,
                                    }) }, { children: options.name })), _jsx("button", Object.assign({ type: "button", className: classNames({
                                        'w-8 h-8 flex  items-center justify-center rounded-full hover:bg-white/20 transition-colors': !useTailwindPrefix,
                                        'tw-w-8 tw-h-8 tw-flex tw-items-center tw-justify-center tw-rounded-full hover:tw-bg-white/20 tw-transition-colors': useTailwindPrefix,
                                    }), onClick: () => setIsOpen(false) }, { children: _jsx("svg", Object.assign({ viewBox: "0 0 24 24", className: classNames({ 'w-5 h-5': !useTailwindPrefix, 'tw-w-5 tw-h-5': useTailwindPrefix }), fill: "none", stroke: "currentColor", strokeWidth: "2.5" }, { children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", d: "M19.5 12h-15" }) })) }))] })), _jsxs("div", Object.assign({ className: classNames(styles.messages, {
                                'flex-1 overflow-y-auto p-4 pb-6 flex flex-col-reverse gap-6': !useTailwindPrefix,
                                'tw-flex-1 tw-overflow-y-auto tw-p-4 tw-pb-6 tw-flex tw-flex-col-reverse tw-gap-6': useTailwindPrefix,
                            }) }, { children: [isFetching && (_jsx(ChatBubble, Object.assign({ role: "ASSISTANT" }, { children: _jsxs("div", Object.assign({ className: classNames({
                                            'flex items-center gap-1': !useTailwindPrefix,
                                            'tw-flex tw-items-center tw-gap-1': useTailwindPrefix,
                                        }) }, { children: [_jsx("span", { className: styles.dot, style: { animationDelay: '0s' } }), _jsx("span", { className: styles.dot, style: { animationDelay: '0.2s' } }), _jsx("span", { className: styles.dot, style: { animationDelay: '0.4s' } })] })) }))), history === null || history === void 0 ? void 0 : history.map((msg, index) => {
                                    var _a, _b, _c, _d;
                                    return (_jsxs(ChatBubble.Context, Object.assign({ role: msg.role, messageKey: msg.id, className: classNames({
                                            relative: !useTailwindPrefix,
                                            'tw-relative': useTailwindPrefix,
                                        }) }, { children: [_jsx(ChatBubble.Content, { children: _jsx(ReactMarkdown, { children: msg.content }) }), _jsx(ChatBubble.Rate, { className: classNames(Object.assign({}, (((_a = msg.data) === null || _a === void 0 ? void 0 : _a.skills) || ((_b = msg.data) === null || _b === void 0 ? void 0 : _b.dataRequired)
                                                    ? {
                                                        '-mt-1': !useTailwindPrefix,
                                                        'tw--mt-1': useTailwindPrefix,
                                                    }
                                                    : {
                                                        'absolute -bottom-5': !useTailwindPrefix,
                                                        'tw-absolute tw--bottom-5': useTailwindPrefix,
                                                    }))) }), drawSkills(msg)] }), (_d = (_c = msg.id) !== null && _c !== void 0 ? _c : msg.timestamp) !== null && _d !== void 0 ? _d : index));
                                }), _jsx(ChatBubble, Object.assign({ role: "ASSISTANT" }, { children: intl.formatMessage({
                                        id: 'maia_chatbot_welcomeMessage',
                                    }, Object.assign({ name: options.name || 'MaIA' }, (options.accountName && { accountName: options.accountName }))) }))] })), _jsx(ChatForm, {})] })) })))] })));
};
export default Bot;
